import React, { useState, useEffect, useCallback } from "react";
import "./Dashboard.css";
import { useMsal } from "@azure/msal-react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import Box from "@mui/system/Box";
import UserOnboarding from "../UserOnboarding/UserOnboarding";
import Admin from "../Admin/Admin";
import Navbar from "../Navbar/Navbar";
import Sidebar from "../Sidebar/Sidebar";
import DashboardHome from "../DashboardHome/DashboardHome";
import SecurityCheck from "../SecurityCheck/SecurityCheck";
import MatchesBoard from "../MatchesBoard/MatchesBoard";
import OfferBoards from "../OfferBoards/OfferBoards";
import LoadBoards from "../LoadBoards/LoadBoards";
import LoadBoardsV2 from "../LoadBoardsV2/LoadBoardsV2";
import CarrierSearch from "../CarrierSearch/CarrierSearch";
import CarrierProfile from "../CarrierProfile/CarrierProfile";
import LoadProfile from "../LoadProfile/LoadProfile";
import QuickActions from "../Modals/QuickActions/QuickActions";
import LoadBookingModal from "../Modals/LoadBookingModal/LoadBookingModal";
import CarrierOffersModal from "../Modals/CarrierOffersModal/CarrierOffersModal";
import BounceModal from "../Modals/BounceModal/BounceModal";
import CapacityModal from "../Modals/CapacityModal/CapacityModal";
import CarrierContactsModal from "../Modals/CarrierContactsModal/CarrierContactsModal";
import LoadContactsModal from "../Modals/LoadContactsModal/LoadContactsModal";
import TableModalContainer from "../Modals/TableModalContainer/TableModalContainer";
import useLoadBoardsReceiver from "../../hooks/useLoadBoardsReceiver/useLoadBoardsReceiver";
import useShotClockReceiver from "../../hooks/useShotClockReceiver/useShotClockReceiver";
import useOfferBoardsReceiver from "../../hooks/useOfferBoardsReceiver/useOfferBoardsReceiver";
import useSidebarReceiver from "../../hooks/useSidebarReceiver/useSidebarReceiver";
import AlertFunction from "../../hooks/useContext/alertContext/alertContext";
import { getToken, getUser } from "../../utils/authHelpers";
import { buildSearchParams } from "../../utils/requestHelpers";
import { handleBookmarkHelper, handleRecentlyViewedHelper } from "../../utils/recentlyViewedHelpers";
import { SecurityService } from "../../services/SecurityService";

export const LockedLoadsContext = React.createContext();
export const ActiveUserContext = React.createContext();
export const UserPermissionsContext = React.createContext();
export const UserRolesContext = React.createContext();
export const AlertContext = React.createContext({
  alert: null,
  alertText: null,
  success: () => { },
  error: () => { },
  warning: () => { },
  clear: () => { },
});
export const PanelSizeContext = React.createContext({
  sidebarWidth: null,
});

export default function Dashboard() {
  const { instance } = useMsal();
  let navigate = useNavigate();
  let location = useLocation();
  const [activeUser, setActiveUser] = useState({});
  const [userPermissions, setUserPermissions] = useState({});
  const [userRoles, setUserRoles] = useState([]);
  const [sidebarWidth, setSidebarWidth] = useState(280);
  const [activeTableModal, setActiveTableModal] = useState(null);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [refreshPage, setRefreshPage] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [useLoadBoardsV2, setUseLoadBoardsV2] = useState(() => {
    const saved = localStorage.getItem("useLoadBoardsV2");
    return saved !== null ? JSON.parse(saved) : true; // Default to V2
  });
  const { lockedLoads } = useShotClockReceiver();
  const {
    isLoadBoardLoading,
    allLoads,
    deleteNewLoads,
    updateLoadField,
    newcomer
  } = useLoadBoardsReceiver(isLoggedIn);
  const {
    isLoading,
    carrierLoads,
    newItem,
    pendingLoadsCount,
    setRefreshLoads,
    regions,
    selectedRegions,
    setSelectedRegions,
    customers,
    selectedCustomers,
    setSelectedCustomers,
    isAdminUser
  } = useOfferBoardsReceiver(activeUser, userPermissions, isLoggedIn);
  const {
    carrierNetwork,
    viewingUser,
    setViewingUser
  } = useSidebarReceiver(activeUser);
  const { alert, alertText, success, error, warning, clear } = AlertFunction();

  const excludedPaths = [
    "freight-matching",
    "quick-actions",
    "load-booking",
    "carrier-offer",
    "bounce-carrier",
    "carrier-capacity",
    "carrier-contact",
    "load-contact",
    "load-profile",
    "carrier-profile",
    "carrier-search",
    "offer-boards"
  ];

  const handleTableAction = useCallback((ev) => {
    if (ev) {
      const params = buildSearchParams({
        loadId: ev?.loadId,
        carrierId: ev?.carrierId,
        offerAmount: ev?.offerAmount,
        carrierOfferId: ev?.carrierOfferId,
        action: ev?.subaction,
        type: ev?.type,
        tab: ev?.tab,
      });

      if (excludedPaths.includes(ev?.action)) {
        const path = ev?.action === "freight-matching" || ev?.action === "carrier-search" ? ev?.action : `${ev?.action}?${params}`;
        openWindow(path, true, ["load-booking", "carrier-offer", "load-contact"].includes(ev?.action));
      } else {
        setActiveTableModal(ev);
      }
    } else {
      setActiveTableModal(null);
    }
  }, [activeTableModal]);

  const openWindow = (path, rfshPage, rfshLoads) => {
    const quickWindow = window.open(`/dashboard/${path}`, "_blank", getWindowFeatures());
    const checkWindowClosed = setInterval(() => {
      if (quickWindow.closed) {
        if (localStorage.getItem("accessorials")) localStorage.removeItem("accessorials");
        if (localStorage.getItem("lane")) localStorage.removeItem("lane");
        if (localStorage.getItem("carrier-contact")) localStorage.removeItem("carrier-contact");

        setRefreshPage(rfshPage);
        setRefreshLoads(rfshLoads);
        clearInterval(checkWindowClosed);
      }
    }, 1000);
  };

  const getWindowFeatures = () => {
    const windowsSize = "width=1170,height=800";
    return `${windowsSize},left=${window.screen.width / 2 - 640},top=${window.screen.height / 2 - 400}`;
  };

  const handleRecentlyViewed = item => {
    let recentlyViewedUpdated = handleRecentlyViewedHelper(item, recentlyViewed);
    setRecentlyViewed(recentlyViewedUpdated);
  };

  const handleBookmark = (index, item) => {
    let recentlyViewedUpdated = handleBookmarkHelper(index, item, recentlyViewed);
    setRecentlyViewed(recentlyViewedUpdated);
  };

  useEffect(() => {
    SecurityService.GetUserRoles(instance).then(res => {
      setUserRoles(res);
    }).catch(error => {
      console.error(error);
      warning(error);
    });

    SecurityService.GetUserPermissions(instance).then(res => {
      if (res && Object.keys(res).length) {
        setUserPermissions(res);
      } else {
        setUserPermissions({});
        navigate("/dashboard/user-onboarding");
      }
    }).catch(error => {
      setUserPermissions({});
      navigate("/dashboard/user-onboarding");
      console.error(error);
      warning(error);
    });

    const getActiveUser = async () => {
      const token = await getToken(instance);
      const userId = getUser(token).oid;
      SecurityService.GetUser(instance, userId).then(res => {
        setActiveUser(res);
        setIsLoggedIn(true);
      }).catch(error => {
        navigate("/dashboard/user-onboarding");
        console.error(error);
        warning(error);
      });
    };

    const getData = () => {
      if (localStorage.getItem("recentlyViewed")) {
        setRecentlyViewed(JSON.parse(localStorage.getItem("recentlyViewed")));
      }
    };

    getActiveUser();
    getData();

    window.addEventListener("storage", getData);
    return () => window.removeEventListener("storage", getData);
  }, []);

  return (
    <Box className="Dashboard-container">
      <LockedLoadsContext.Provider value={lockedLoads}>
        <ActiveUserContext.Provider value={activeUser}>
          <UserPermissionsContext.Provider value={userPermissions}>
            <UserRolesContext.Provider value={userRoles}>
              <AlertContext.Provider value={{ alert, alertText, success, error, warning, clear }}>
                <PanelSizeContext.Provider value={{ sidebarWidth }}>
                  <TableModalContainer
                    activeTableModal={activeTableModal}
                    setActiveTableModal={setActiveTableModal}
                    setRefreshPage={setRefreshPage}
                    setRefreshLoads={setRefreshLoads}
                  />
                  {activeUser.userIdMcld && !excludedPaths.some(e => location.pathname.includes(e)) &&
                    <SecurityCheck
                      componentName="CarrierSearch"
                      action="Read"
                    >
                      <Sidebar
                        carrierNetwork={carrierNetwork}
                        viewingUser={viewingUser}
                        setViewingUser={setViewingUser}
                        sidebarWidth={sidebarWidth}
                        setSidebarWidth={setSidebarWidth}
                        recentlyViewed={recentlyViewed}
                        handleBookmark={handleBookmark}
                        handleTableAction={handleTableAction}
                      />
                    </SecurityCheck>
                  }
                  <Box className="Dashboard">
                    {!excludedPaths.some(e => location.pathname.includes(e)) &&
                      <Navbar
                        pendingLoadsCount={pendingLoadsCount}
                        handleTableAction={handleTableAction}
                      />
                    }
                    <Routes>
                      <Route path="/" element={<></>} />
                      {activeUser.userId &&
                        <Route path="home" element={
                          <DashboardHome />
                        } />
                      }
                      <Route path="user-onboarding" element={
                        <UserOnboarding />
                      } />
                      <Route path="carrier-search" element={
                        <SecurityCheck
                          componentName="CarrierSearch"
                          action="Read"
                        >
                          <CarrierSearch
                            handleTableAction={handleTableAction}
                          />
                        </SecurityCheck>
                      } />
                      <Route path="load-boards" element={
                        <SecurityCheck
                          componentName="CarrierSearch"
                          action="Read"
                        >
                          {useLoadBoardsV2 ? (
                            <LoadBoardsV2
                              isAdminUser={isAdminUser}
                              isLoadBoardLoading={isLoadBoardLoading}
                              allLoads={allLoads}
                              deleteNewLoads={deleteNewLoads}
                              onLoadFieldUpdate={updateLoadField}
                              handleTableAction={handleTableAction}
                              useLoadBoardsV2={useLoadBoardsV2}
                              setUseLoadBoardsV2={setUseLoadBoardsV2}
                            />
                          ) : (
                            <LoadBoards
                              isAdminUser={isAdminUser}
                              isLoadBoardLoading={isLoadBoardLoading}
                              allLoads={allLoads}
                              deleteNewLoads={deleteNewLoads}
                              onLoadFieldUpdate={updateLoadField}
                              handleTableAction={handleTableAction}
                              useLoadBoardsV2={useLoadBoardsV2}
                              setUseLoadBoardsV2={setUseLoadBoardsV2}
                            />
                          )}
                        </SecurityCheck>
                      } />
                      <Route path="offer-boards" element={
                        <SecurityCheck
                          componentName="CarrierOffersDashboard"
                          action="Read"
                        >
                          <OfferBoards
                            isLoading={isLoading}
                            carrierLoads={carrierLoads}
                            newItem={newItem}
                            setRefreshLoads={setRefreshLoads}
                            regions={regions}
                            selectedRegions={selectedRegions}
                            setSelectedRegions={setSelectedRegions}
                            customers={customers}
                            selectedCustomers={selectedCustomers}
                            setSelectedCustomers={setSelectedCustomers}
                            isAdminUser={isAdminUser}
                            refreshPage={refreshPage}
                            setRefreshPage={setRefreshPage}
                            onLoadFieldUpdate={updateLoadField}
                            handleTableAction={handleTableAction}
                          />
                        </SecurityCheck>
                      } />
                      <Route path="freight-matching" element={
                        <SecurityCheck
                          componentName="CarrierSearch"
                          action="Read"
                        >
                          <MatchesBoard
                            handleTableAction={handleTableAction}
                          />
                        </SecurityCheck>
                      } />
                      <Route path="load-profile" element={
                        <LoadProfile
                          newcomer={newcomer}
                          refreshPage={refreshPage}
                          setRefreshPage={setRefreshPage}
                          onLoadFieldUpdate={updateLoadField}
                          handleTableAction={handleTableAction}
                          handleRecentlyViewed={handleRecentlyViewed}
                        />
                      } />
                      <Route path="carrier-profile" element={
                        <CarrierProfile
                          refreshPage={refreshPage}
                          setRefreshPage={setRefreshPage}
                          handleTableAction={handleTableAction}
                          handleRecentlyViewed={handleRecentlyViewed}
                        />
                      } />
                      <Route path="quick-actions" element={
                        <SecurityCheck
                          componentName="CarrierSearch"
                          action="Read"
                        >
                          <QuickActions />
                        </SecurityCheck>
                      } />
                      <Route path="load-booking" element={
                        <SecurityCheck
                          componentName="CarrierSearch"
                          action="Read"
                        >
                          <LoadBookingModal
                            handleRecentlyViewed={handleRecentlyViewed}
                          />
                        </SecurityCheck>
                      } />
                      <Route path="carrier-offer" element={
                        <SecurityCheck
                          componentName="CarrierSearch"
                          action="Read"
                        >
                          <CarrierOffersModal
                            handleTableAction={handleTableAction}
                          />
                        </SecurityCheck>
                      } />
                      <Route path="bounce-carrier" element={
                        <SecurityCheck
                          componentName="CarrierSearch"
                          action="Read"
                        >
                          <BounceModal />
                        </SecurityCheck>
                      } />
                      <Route path="carrier-capacity" element={
                        <SecurityCheck
                          componentName="CarrierSearch"
                          action="Read"
                        >
                          <CapacityModal />
                        </SecurityCheck>
                      } />
                      <Route path="carrier-contact" element={
                        <SecurityCheck
                          componentName="CarrierSearch"
                          action="Read"
                        >
                          <CarrierContactsModal />
                        </SecurityCheck>
                      } />
                      <Route path="load-contact" element={
                        <SecurityCheck
                          componentName="CarrierSearch"
                          action="Read"
                        >
                          <LoadContactsModal />
                        </SecurityCheck>
                      } />
                      <Route path="admin/*" element={
                        <Admin />
                      } />
                    </Routes>
                  </Box>
                </PanelSizeContext.Provider>
              </AlertContext.Provider>
            </UserRolesContext.Provider>
          </UserPermissionsContext.Provider>
        </ActiveUserContext.Provider>
      </LockedLoadsContext.Provider>
    </Box>
  );
}